import React from 'react';
import styled from 'styled-components';
import { theme } from '../styles/theme';

/**
 * WorkingHoursEditor
 * ------------------
 * Editor de horário de atendimento reutilizável (membro e conector).
 * Modelo de UI: uma janela (início/fim) por dia da semana; dia desmarcado = fechado.
 * O backend usa o formato { enabled, days: { mon: [{start,end}] } }; a conversão
 * de/para o formato do formulário fica nos helpers exportados abaixo.
 *
 * Props:
 *  - value: estado do formulário { enabled, days: { mon: {start,end} } }
 *  - onChange: (newValue) => void
 *  - t: função de tradução do namespace do chamador (chaves workingHours e weekdays)
 *  - enableLabel / hint: textos opcionais para sobrescrever os defaults
 */

export const WEEKDAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

const DEFAULT_WEEKDAY_LABELS = {
  mon: 'Segunda', tue: 'Terça', wed: 'Quarta', thu: 'Quinta',
  fri: 'Sexta', sat: 'Sábado', sun: 'Domingo',
};

// Estado vazio (nenhum horário = sempre disponível).
export const EMPTY_WORKING_HOURS = { enabled: false, days: {} };

/** Converte workingHours do backend ({days:{mon:[{start,end}]}}) para o form. */
export function whFromBackend(wh) {
  if (!wh || wh.enabled === false || !wh.days) return { enabled: false, days: {} };
  const days = {};
  for (const d of WEEKDAYS) {
    const win = Array.isArray(wh.days[d]) ? wh.days[d][0] : null;
    if (win && win.start && win.end) days[d] = { start: win.start, end: win.end };
  }
  return { enabled: true, days };
}

/** Converte o form para o backend. Retorna null quando desativado (limpa horário). */
export function whToBackend(form) {
  if (!form || !form.enabled) return null;
  const days = {};
  for (const d of WEEKDAYS) {
    const win = form.days[d];
    if (win && win.start && win.end) days[d] = [{ start: win.start, end: win.end }];
  }
  return { enabled: true, days };
}

export default function WorkingHoursEditor({ value, onChange, t, enableLabel, hint }) {
  const wh = value || EMPTY_WORKING_HOURS;

  // Rótulo de dia: usa t('weekdays.<d>') se houver, senão default pt-BR.
  const dayLabel = (d) => {
    if (t) {
      const translated = t(`weekdays.${d}`, { defaultValue: '' });
      if (translated) return translated;
    }
    return DEFAULT_WEEKDAY_LABELS[d];
  };

  const setEnabled = (enabled) => onChange({ ...wh, enabled });

  const toggleDay = (day) => {
    const days = { ...wh.days };
    if (days[day]) delete days[day];
    else days[day] = { start: '08:00', end: '18:00' };
    onChange({ ...wh, days });
  };

  const setTime = (day, field, val) => {
    onChange({
      ...wh,
      days: { ...wh.days, [day]: { ...wh.days[day], [field]: val } },
    });
  };

  return (
    <>
      <CheckItem>
        <input
          type="checkbox"
          id="wh-enabled"
          checked={!!wh.enabled}
          onChange={(e) => setEnabled(e.target.checked)}
        />
        <label htmlFor="wh-enabled">
          {enableLabel || (t && t('workingHoursEnable')) || 'Definir horário de atendimento'}
        </label>
      </CheckItem>
      <HintText>
        {hint || (t && t('workingHoursHint')) ||
          'Sem horário definido, disponível a qualquer momento.'}
      </HintText>
      {wh.enabled && (
        <Grid>
          {WEEKDAYS.map((day) => {
            const win = wh.days[day];
            return (
              <DayRow key={day}>
                <DayToggle>
                  <input
                    type="checkbox"
                    id={`wh-${day}`}
                    checked={!!win}
                    onChange={() => toggleDay(day)}
                  />
                  <label htmlFor={`wh-${day}`}>{dayLabel(day)}</label>
                </DayToggle>
                {win ? (
                  <DayTimes>
                    <TimeInput type="time" value={win.start} onChange={(e) => setTime(day, 'start', e.target.value)} />
                    <span>–</span>
                    <TimeInput type="time" value={win.end} onChange={(e) => setTime(day, 'end', e.target.value)} />
                  </DayTimes>
                ) : (
                  <DayClosed>{(t && t('workingHoursClosed')) || 'Fechado'}</DayClosed>
                )}
              </DayRow>
            );
          })}
        </Grid>
      )}
    </>
  );
}

const CheckItem = styled.div`display: flex; align-items: center; gap: 8px; font-size: ${theme.typography.sm}; cursor: pointer; label { cursor: pointer; }`;
const HintText = styled.div`font-size: ${theme.typography.xs}; color: ${theme.colors.textMuted}; margin-top: 4px;`;
const Grid = styled.div`display: flex; flex-direction: column; gap: 6px; margin-top: ${theme.spacing[2]}; border: 1px solid ${theme.colors.border}; border-radius: ${theme.radii.md}; padding: ${theme.spacing[3]};`;
const DayRow = styled.div`display: flex; align-items: center; justify-content: space-between; gap: ${theme.spacing[3]};`;
const DayToggle = styled.div`display: flex; align-items: center; gap: 8px; font-size: ${theme.typography.sm}; min-width: 120px; label { cursor: pointer; }`;
const DayTimes = styled.div`display: flex; align-items: center; gap: 6px;`;
const DayClosed = styled.span`font-size: ${theme.typography.xs}; color: ${theme.colors.textMuted}; font-style: italic;`;
const TimeInput = styled.input`font-size: ${theme.typography.sm}; padding: 4px 6px; border: 1px solid ${theme.colors.border}; border-radius: ${theme.radii.sm}; color: ${theme.colors.textPrimary};`;
